<template>
  <v-row>
    <v-col cols="12">
      <div class="bm-page-head">
        <div class="bm-title">Setting Budget Monitoring</div>
        <div class="bm-subtitle">
          Berlaku pada perhitungan berikutnya. Setiap perubahan dicatat.
        </div>
        <v-spacer></v-spacer>
        <router-link to="/report/budget-monitoring" class="bm-tiny text-decoration-none mr-2"
          >← Kembali ke daftar</router-link
        >
        <v-btn color="primary" :loading="savingSetting" :disabled="!dirty" @click="saveSetting"
          >Simpan perubahan</v-btn
        >
      </div>

      <v-row align="start">
        <!-- daftar isi -->
        <v-col cols="12" md="2" class="bm-toc">
          <a
            v-for="s in sections"
            :key="s.id"
            :href="'#' + s.id"
            class="bm-toc-item"
            :class="{ 'bm-toc-item--on': active === s.id }"
            @click.prevent="jump(s.id)"
            >{{ s.title }}</a
          >
        </v-col>

        <v-col cols="12" md="10">
          <!-- 1 ambang -->
          <v-card id="ambang" class="mb-4">
            <div class="bm-sec">
              <div>
                <div class="bm-sec-title">Ambang band</div>
                <div class="bm-sec-sub">
                  Rasio = pemakaian ÷ budget. Band menentukan warna di daftar dan kapan email
                  dikirim.
                </div>
              </div>
            </div>
            <div class="pa-5">
              <v-row dense>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field
                    v-model.number="form.yellowFrom"
                    label="Kuning mulai >"
                    suffix="%"
                    type="number"
                    outlined
                    dense
                    hint="Hijau selama rasio ≤ nilai ini."
                    persistent-hint
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field
                    v-model.number="form.redFrom"
                    label="Merah mulai >"
                    suffix="%"
                    type="number"
                    outlined
                    dense
                    hint="Melebihi budget."
                    persistent-hint
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field
                    v-model.number="form.lowConfidenceFrom"
                    label="Tandai estimasi tinggi bila >"
                    suffix="%"
                    type="number"
                    outlined
                    dense
                    hint="Porsi pemakaian yang dialokasikan proporsional."
                    persistent-hint
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="bm-tiny mb-2">Pratinjau band</div>
                  <div class="bm-band-preview">
                    <div
                      :style="{ width: previewPct(form.yellowFrom) + '%', background: '#4CAF50' }"
                    ></div>
                    <div
                      :style="{
                        width: previewPct(form.redFrom) - previewPct(form.yellowFrom) + '%',
                        background: '#FB8C00',
                      }"
                    ></div>
                    <div
                      :style="{
                        width: 100 - previewPct(form.redFrom) + '%',
                        background: '#E53935',
                      }"
                    ></div>
                  </div>
                  <div class="bm-tiny d-flex justify-space-between mt-1">
                    <span>0 %</span><span>{{ form.yellowFrom }} %</span
                    ><span>{{ form.redFrom }} %</span><span>{{ previewMax }} %</span>
                  </div>
                </v-col>
              </v-row>
              <div v-if="thresholdError" class="error--text bm-tiny mt-2">{{ thresholdError }}</div>
            </div>
          </v-card>

          <!-- 2 apa yang dihitung -->
          <v-card id="hitung" class="mb-4">
            <div class="bm-sec">
              <div>
                <div class="bm-sec-title">Apa yang dihitung</div>
                <div class="bm-sec-sub">
                  Komponen yang masuk pemakaian dan PO / job mana yang dipantau.
                </div>
              </div>
            </div>
            <div class="px-5 py-2">
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-switch v-model="form.includeStockUsage" inset dense hide-details class="mt-2">
                    <template v-slot:label>
                      <div>
                        <div>Pemakaian stok (MR) masuk pemakaian</div>
                        <div class="bm-tiny">Mati = hanya belanja PO supplier yang dihitung.</div>
                      </div>
                    </template>
                  </v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch v-model="form.includeFreight" inset dense hide-details class="mt-2">
                    <template v-slot:label>
                      <div>
                        <div>Freight PO supplier masuk belanja</div>
                        <div class="bm-tiny">PPN tidak pernah dihitung.</div>
                      </div>
                    </template>
                  </v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    v-model="form.includeBomProjection"
                    inset
                    dense
                    hide-details
                    class="mt-2"
                  >
                    <template v-slot:label>
                      <div>
                        <div>Hitung proyeksi sisa BOM</div>
                        <div class="bm-tiny">
                          Sisa BOM (belum di-MR / PO / PR) × harga terakhir menjadi kolom "→ akhir"
                          dan kartu Proyeksi akhir. Tidak masuk rasio dan tidak memicu email.
                        </div>
                      </div>
                    </template>
                  </v-switch>
                </v-col>
                <v-col cols="12" md="6" class="mt-4">
                  <v-row dense align="center">
                    <v-col cols="6" md="5">
                      <v-text-field
                        v-model.number="form.closedJobGraceDays"
                        label="Job closed dipantau"
                        suffix="hari"
                        type="number"
                        outlined
                        dense
                        hide-details
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6" md="7" class="bm-tiny"
                      >Setelah lewat, belanja baru job closed diabaikan.</v-col
                    >
                  </v-row>
                </v-col>
                <v-col cols="12" md="6" class="mt-4">
                  <v-row dense align="center">
                    <v-col cols="6" md="5">
                      <v-text-field
                        v-model.number="form.poLookbackMonths"
                        label="PO CRM dipantau"
                        suffix="bulan"
                        type="number"
                        outlined
                        dense
                        hide-details
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6" md="7" class="bm-tiny"
                      >PO lebih lama dari ini tidak dihitung dan tidak dikirimi email.</v-col
                    >
                  </v-row>
                </v-col>
              </v-row>
            </div>
          </v-card>

          <!-- 3 penerima -->
          <v-card id="penerima" class="mb-4">
            <div class="bm-sec">
              <div>
                <div class="bm-sec-title">Penerima email</div>
                <div class="bm-sec-sub">
                  Dikirim lewat n8n saat PO berpindah band. Placeholder:
                  <span v-for="(help, ph) in placeholderHelp" :key="ph"
                    ><code>{{ ph }}</code> {{ help }} ·
                  </span>
                </div>
              </div>
              <v-btn outlined small color="primary" @click="testDialog = true"
                >Kirim email uji</v-btn
              >
            </div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th style="width: 150px">Saat masuk band</th>
                  <th>Kepada</th>
                  <th style="width: 200px">CC</th>
                  <th style="width: 100px">Status</th>
                  <th style="width: 70px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="t in types" :key="t">
                  <td>
                    <v-chip x-small dark :color="bandColor(typeMeta[t].band)">{{
                      typeMeta[t].title
                    }}</v-chip>
                  </td>
                  <td>
                    <span v-for="r in recipientsOf(t, 'TO')" :key="r.email" class="mr-1">
                      <v-chip
                        x-small
                        :color="isPlaceholder(r.email) ? 'blue lighten-5' : 'grey lighten-3'"
                        :class="isPlaceholder(r.email) ? 'blue--text text--darken-2 bm-mono' : ''"
                        >{{ r.email }}</v-chip
                      >
                    </span>
                    <span v-if="!recipientsOf(t, 'TO').length" class="grey--text">—</span>
                  </td>
                  <td>
                    <span v-for="r in recipientsOf(t, 'CC')" :key="r.email" class="mr-1">
                      <v-chip
                        x-small
                        :color="isPlaceholder(r.email) ? 'blue lighten-5' : 'grey lighten-3'"
                        :class="isPlaceholder(r.email) ? 'blue--text text--darken-2 bm-mono' : ''"
                        >{{ r.email }}</v-chip
                      >
                    </span>
                    <span v-if="!recipientsOf(t, 'CC').length" class="grey--text">—</span>
                  </td>
                  <td>
                    <v-chip
                      x-small
                      :color="recipientsOf(t, 'TO').length ? 'green lighten-5' : 'grey lighten-3'"
                      :class="recipientsOf(t, 'TO').length ? 'green--text text--darken-3' : ''"
                      >{{ recipientsOf(t, "TO").length ? "Aktif" : "Nonaktif" }}</v-chip
                    >
                  </td>
                  <td><a class="bm-tiny" @click="editRecipients(t)">Ubah</a></td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>

          <!-- 4 override -->
          <v-card id="override" class="mb-4">
            <div class="bm-sec">
              <div>
                <div class="bm-sec-title">Override margin</div>
                <div class="bm-sec-sub">
                  Untuk PO tanpa margin di CRM ({{ noBudgetCount }} PO). Budget = subtotal × (1 −
                  margin ini). Margin CRM menang bila kemudian diisi.
                </div>
              </div>
              <v-btn outlined small color="primary" @click="addOverride">+ Tambah override</v-btn>
            </div>
            <v-simple-table dense class="bm-table">
              <thead>
                <tr>
                  <th style="width: 140px">PO CRM</th>
                  <th>Customer · judul</th>
                  <th class="text-right" style="width: 80px">Margin</th>
                  <th class="text-right" style="width: 140px">Budget hasil</th>
                  <th style="width: 260px">Alasan · oleh</th>
                  <th style="width: 70px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="o in overrides" :key="o.crmPoId">
                  <td>
                    <div class="bm-cell">
                      <router-link
                        :to="'/report/budget-monitoring/po/' + o.crmPoId"
                        class="font-weight-medium text-decoration-none"
                        >{{ o.poNumber || "#" + o.crmPoId }}</router-link
                      >
                      <span v-if="crmWins(o)" class="bm-tiny orange--text text--darken-3"
                        >tidak dipakai — CRM terisi</span
                      >
                    </div>
                  </td>
                  <td>
                    <div class="bm-cell">
                      <span>{{ o.customer || "—" }}</span>
                      <span class="bm-tiny">{{ o.title || "—" }}</span>
                    </div>
                  </td>
                  <td class="text-right bm-num">{{ formatPct(o.marginPercent) }}</td>
                  <td class="text-right bm-num">
                    {{
                      o.subtotalIdr
                        ? formatIdr(Number(o.subtotalIdr) * (1 - Number(o.marginPercent) / 100))
                        : "—"
                    }}
                  </td>
                  <td>
                    <div class="bm-cell">
                      <span :title="o.reason">{{ o.reason || "—" }}</span>
                      <span class="bm-tiny"
                        >{{ o.updatedBy || "—" }} · {{ formatDateTime(o.updatedAt) }}</span
                      >
                    </div>
                  </td>
                  <td><a class="bm-tiny" @click="editOverride(o)">Ubah</a></td>
                </tr>
                <tr v-if="!overrides.length">
                  <td colspan="6" class="text-center grey--text py-6">
                    Belum ada override. PO tanpa margin tampil sebagai “Tanpa budget” di daftar.
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>

          <!-- 5 kurs & jadwal -->
          <v-card id="kurs" class="mb-4">
            <div class="bm-sec">
              <div>
                <div class="bm-sec-title">Kurs &amp; jadwal</div>
                <div class="bm-sec-sub">
                  Informasi sistem. Kurs diambil dari tabel kurs accounting pada tanggal PO
                  supplier; bila tidak ada, baris dihitung kurs 1 dan PO ditandai
                  <v-icon x-small color="error">mdi-currency-usd-off</v-icon>.
                </div>
              </div>
              <v-btn text small color="primary" :to="'/setting/currency'"
                >Buka halaman Currency</v-btn
              >
            </div>
            <div class="pa-5">
              <v-row dense>
                <v-col cols="12" md="4">
                  <div class="bm-tiny">Jadwal hitung otomatis</div>
                  <div :title="scheduleCron">{{ scheduleLabel(scheduleCron) }}</div>
                  <div class="bm-tiny">
                    Perlu angka lebih baru? Pakai "Hitung ulang" di daftar, detail PO, atau detail
                    job.
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="bm-tiny">Perhitungan terakhir</div>
                  <div v-if="lastRun">
                    {{ formatDateTime(lastRun.finishedAt || lastRun.startedAt) }} ·
                    {{ lastRun.status }}
                  </div>
                  <div v-else class="grey--text">belum ada</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="bm-tiny">Konversi</div>
                  <div>
                    Subtotal PO CRM pada tanggal PO; PO supplier pada tanggal PO supplier; PR tanpa
                    PO pada tanggal PR.
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-col>

    <budget-recipient-dialog
      v-model="recipientDialog"
      :notification-type="editingType"
      :recipients="recipients[editingType] || []"
      @saved="loadRecipients"
    ></budget-recipient-dialog>
    <budget-override-dialog
      v-model="overrideDialog"
      :override="editingOverride"
      :po="presetPo"
      @saved="loadOverrides"
    ></budget-override-dialog>
    <budget-test-email-dialog v-model="testDialog"></budget-test-email-dialog>
  </v-row>
</template>

<script>
import { mapGetters } from "vuex";
import BudgetRecipientDialog from "@/components/budget/BudgetRecipientDialog.vue";
import BudgetOverrideDialog from "@/components/budget/BudgetOverrideDialog.vue";
import BudgetTestEmailDialog from "@/components/budget/BudgetTestEmailDialog.vue";
import {
  bandColor,
  formatDateTime,
  formatIdr,
  formatPct,
  isPlaceholder,
  NOTIFICATION_TYPE_META,
  NOTIFICATION_TYPES,
  PLACEHOLDER_HELP,
  scheduleLabel,
} from "@/utils/budgetMonitor";
import "@/components/budget/budget-monitor.css";

const DEFAULTS = {
  yellowFrom: 80,
  redFrom: 100,
  lowConfidenceFrom: 50,
  includeFreight: true,
  includeStockUsage: true,
  includeBomProjection: true,
  poLookbackMonths: 18,
  closedJobGraceDays: 90,
};

export default {
  name: "budget-monitoring-setting",
  components: { BudgetRecipientDialog, BudgetOverrideDialog, BudgetTestEmailDialog },
  data: () => ({
    scheduleCron: "",
    form: { ...DEFAULTS },
    saved: { ...DEFAULTS },
    savingSetting: false,
    recipients: {},
    overrides: [],
    noBudgetCount: 0,
    types: NOTIFICATION_TYPES,
    typeMeta: NOTIFICATION_TYPE_META,
    placeholderHelp: PLACEHOLDER_HELP,
    sections: [
      { id: "ambang", title: "Ambang band" },
      { id: "hitung", title: "Apa yang dihitung" },
      { id: "penerima", title: "Penerima email" },
      { id: "override", title: "Override margin" },
      { id: "kurs", title: "Kurs & jadwal" },
    ],
    active: "ambang",
    recipientDialog: false,
    editingType: "BUDGET_YELLOW",
    overrideDialog: false,
    editingOverride: null,
    presetPo: null,
    testDialog: false,
  }),
  computed: {
    ...mapGetters("budgetMonitor", ["lastRun"]),
    dirty() {
      return JSON.stringify(this.form) !== JSON.stringify(this.saved);
    },
    previewMax() {
      return Math.max(120, Number(this.form.redFrom || 0) + 20);
    },
    thresholdError() {
      const y = Number(this.form.yellowFrom);
      const r = Number(this.form.redFrom);
      if (!(y > 0) || !(r > 0)) return "Ambang harus lebih dari 0.";
      if (y >= r) return "Kuning harus lebih kecil dari merah.";
      return "";
    },
  },
  created() {
    this.loadSetting();
    this.loadRecipients();
    this.loadOverrides();
    this.$store.dispatch("budgetMonitor/fetchRuns").catch(() => {});
    if (this.$route.query.po) {
      this.presetPo = { crmPoId: Number(this.$route.query.po) };
      this.overrideDialog = true;
    }
  },
  methods: {
    bandColor,
    isPlaceholder,
    formatIdr,
    formatPct,
    formatDateTime,
    previewPct(v) {
      return Math.max(0, Math.min(100, ((Number(v) || 0) / this.previewMax) * 100));
    },
    jump(id) {
      this.active = id;
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    recipientsOf(type, kind) {
      return (this.recipients[type] || []).filter(r =>
        kind === "CC" ? r.recipientType === "CC" : r.recipientType !== "CC"
      );
    },
    crmWins(o) {
      return (
        o.currentMarginSource &&
        o.currentMarginSource !== "OVERRIDE" &&
        o.currentMarginSource !== "NONE"
      );
    },
    toast(type, message) {
      this.$store.dispatch("toast", { type, message });
    },
    /** editable fields only: the read-only schedule must not make the form dirty */
    formOf(s) {
      const out = { ...DEFAULTS };
      Object.keys(DEFAULTS).forEach(k => {
        if (s && s[k] !== undefined && s[k] !== null) out[k] = s[k];
      });
      return out;
    },
    scheduleLabel,
    async loadSetting() {
      try {
        const s = await this.$store.dispatch("budgetMonitor/fetchSetting");
        if (s) {
          this.scheduleCron = s.scheduleCron || "";
          this.form = this.formOf(s);
          this.saved = { ...this.form };
        }
      } catch (e) {
        this.toast("error", "Gagal memuat setting");
      }
    },
    async saveSetting() {
      if (this.thresholdError) {
        this.toast("warning", this.thresholdError);
        return;
      }
      this.savingSetting = true;
      try {
        const s = await this.$store.dispatch("budgetMonitor/saveSetting", this.form);
        if (s && s.scheduleCron) this.scheduleCron = s.scheduleCron;
        this.form = this.formOf(s || this.form);
        this.saved = { ...this.form };
        this.toast("success", "Setting tersimpan. Berlaku pada perhitungan berikutnya.");
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.message;
        this.toast("error", msg || "Gagal menyimpan setting");
      } finally {
        this.savingSetting = false;
      }
    },
    async loadRecipients() {
      try {
        const list = await this.$store.dispatch("budgetMonitor/fetchRecipients");
        const next = {};
        NOTIFICATION_TYPES.forEach(t => (next[t] = []));
        (list || []).forEach(
          r => (next[r.notificationType] = (r.recipients || []).map(x => ({ ...x })))
        );
        this.recipients = next;
      } catch (e) {
        this.toast("error", "Gagal memuat penerima");
      }
    },
    editRecipients(type) {
      this.editingType = type;
      this.recipientDialog = true;
    },
    async loadOverrides() {
      try {
        this.overrides = (await this.$store.dispatch("budgetMonitor/fetchOverrides")) || [];
      } catch (e) {
        this.overrides = [];
      }
      try {
        const s = await this.$store.dispatch("budgetMonitor/fetchSummary", { includeClosed: true });
        this.noBudgetCount = (s && s.bandCounts && s.bandCounts.NO_BUDGET) || 0;
      } catch (e) {
        this.noBudgetCount = 0;
      }
    },
    addOverride() {
      this.editingOverride = null;
      this.presetPo = null;
      this.overrideDialog = true;
    },
    editOverride(o) {
      this.editingOverride = o;
      this.presetPo = null;
      this.overrideDialog = true;
    },
  },
};
</script>

<style scoped>
.bm-toc {
  position: sticky;
  top: 88px;
}
.bm-toc-item {
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 12px;
  border-radius: 4px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  text-decoration: none;
}
.bm-toc-item--on {
  background: #e3f2fd;
  color: #1565c0;
  font-weight: 500;
}
.bm-band-preview {
  display: flex;
  height: 14px;
  border-radius: 7px;
  overflow: hidden;
}
</style>
